// pricing-calculator/utils/range.ts
// Generic helpers for working with numeric ranges [min, max]

import type { Range } from '@dopomogai/types';

export const ZERO_RANGE: Range = [0, 0];

export function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

export function ensureFiniteNumber(n: unknown, fallback = 0): number {
  const v = typeof n === 'string' ? Number(n) : (n as number);
  return isFiniteNumber(v) ? v : fallback;
}

export function ensureRange(r?: Partial<Range> | null, fallback: Range = ZERO_RANGE): Range {
  if (!r || !Array.isArray(r)) return fallback;
  const [a = fallback[0], b = fallback[1]] = r as Range;
  return [ensureFiniteNumber(a, fallback[0]), ensureFiniteNumber(b, fallback[1])];
}

/**
 * Adds two ranges component-wise.
 */
export function addRange(a: Range, b: Range): Range {
  return [a[0] + b[0], a[1] + b[1]];
}

/**
 * Subtracts two ranges component-wise (a - b).
 */
export function subRange(a: Range, b: Range): Range {
  return [a[0] - b[0], a[1] - b[1]];
}

/**
 * Returns the component-wise max of two ranges.
 */
export function maxRange(a: Range, b: Range): Range {
  return [Math.max(a[0], b[0]), Math.max(a[1], b[1])];
}

/**
 * Sum a list of ranges. If empty, returns [0,0].
 */
export function sumRangeList(ranges: Range[]): Range {
  return ranges.reduce<Range>((acc, r) => addRange(acc, r), ZERO_RANGE);
}

/**
 * Average (midpoint) of a range.
 */
export function avgRange(r: Range): number {
  return (r[0] + r[1]) / 2;
}

/**
 * Scale a range by a scalar or another range.
 * - If factor is a number k, returns [a*k, b*k].
 * - If factor is a range [kMin, kMax], returns the tight bounds over all products:
 *   min/max of {a*kMin, a*kMax, b*kMin, b*kMax}.
 * Works with negative values too (keeps correct ordering).
 */
export function scaleRange(r: Range, factor: number | Range): Range {
  if (Array.isArray(factor)) {
    const [k0, k1] = factor;
    const a = r[0], b = r[1];
    const candidates = [a * k0, a * k1, b * k0, b * k1];
    return [Math.min(...candidates), Math.max(...candidates)];
  }
  return [r[0] * factor, r[1] * factor];
}

/**
 * Maps a function over both ends of a range.
 */
export function mapRange(r: Range, fn: (n: number) => number): Range {
  return [fn(r[0]), fn(r[1])];
}

/**
 * Clamp both ends of a range to [min, max], if provided.
 */
export function clampRange(r: Range, min?: number, max?: number): Range {
  const clamp = (n: number) => {
    let v = n;
    if (isFiniteNumber(min)) v = Math.max(v, min as number);
    if (isFiniteNumber(max)) v = Math.min(v, max as number);
    return v;
  };
  return mapRange(r, clamp);
}

/**
 * Clamp the range only by an upper bound (historical helper).
 */
export function clampRangeToMax(r: Range, max?: number): Range {
  if (!isFiniteNumber(max)) return r;
  return [Math.min(r[0], max as number), Math.min(r[1], max as number)];
}

/**
 * Convert days to weeks.
 */
export function daysToWeeks(r: Range): Range {
  return [r[0] / 7, r[1] / 7];
}

/**
 * Convert weeks to days.
 */
export function weeksToDays(r: Range): Range {
  return [r[0] * 7, r[1] * 7];
}

/**
 * Add a range to a per-key accumulator map.
 */
export function addRangeToRecord(map: Record<string, Range>, key: string, r: Range): void {
  const prev = map[key] || ZERO_RANGE;
  map[key] = addRange(prev, r);
}
